import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Plus, X, Download, Pencil, ArrowLeft, AlertCircle, ClipboardList, FileClock,
} from 'lucide-react';
import {
  BASE, ICON, todayISO, num, fmtINR, fmtNum, formatRegionLocation,
  SectionTitle, exportPoToExcel, DpoBeamBackground, EMBEDDED_CSS, S,
} from './distributorPoShared';

/* ============================================================================
 * Distributor Purchase Orders — create/edit form + home landing. The
 * dedicated "My Recent Purchase Orders" page (history/filters/status
 * actions) lives in DistributorPOHistory.jsx — see distributorPoShared.js
 * for the pieces shared between the two (styles, exportPoToExcel, statusOf,
 * fmt helpers).
 *
 * Product/pricing data comes from distributor_product_master (the real
 * per-distributor-per-platform catalog with margin data); location comes
 * from distributor_master (one row per distributor+platform+REGION — that's
 * its actual unique key, the schema's own existing answer to a distributor
 * supplying multiple regions). Region-quantity columns below are entirely
 * data-driven off however many region rows come back for the chosen
 * distributor+platform, not hardcoded to any one distributor's name.
 *
 * Approval workflow: a PO is created with order_type NULL (invisible to the
 * Accounts/Invoicing queue — po-tracker.js's ?view=pending_so already filters
 * on order_type IS NOT NULL). The KAM prints/shares a copy with the
 * distributor, gets their OK out-of-band, then from "My Recent POs" either
 * assigns Sales Order / Proforma Invoice (which is what actually notifies
 * Accounts) or Rejects it. While still unclassified, a PO can be edited
 * (add/remove lines, change quantities) from that same list.
 * ========================================================================== */

const blankLine = (id) => ({
  id, ean: '', sku_name: '', mrp: 0, case_pack: '',
  platform_margin_pct: 0, distributor_margin_pct: 0,
  qty: '', regionQty: {}, query: '',
});

/* ---- live pricing math — mirrors computeLinePricing() in distributor-po.js ---- */
function priceLine(l, multiRegion) {
  const mrp = num(l.mrp);
  const platformMarginAmount  = mrp * num(l.platform_margin_pct) / 100;
  const platformBillingPrice  = mrp - platformMarginAmount;
  const distributorCommission = (mrp - platformMarginAmount) * num(l.distributor_margin_pct) / 100;
  const billingPrice          = mrp - platformMarginAmount - distributorCommission;
  const qty = multiRegion
    ? Object.values(l.regionQty || {}).reduce((s, v) => s + num(v), 0)
    : num(l.qty);
  return { platformBillingPrice, distributorCommission, billingPrice, qty, amount: billingPrice * qty };
}

export default function DistributorPO({ user }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [distributors, setDistributors] = useState([]);
  const [platforms, setPlatforms]       = useState([]);
  const [regions, setRegions]           = useState([]); // [{region, location, state}]
  const [distributor, setDistributor]   = useState('');
  const [platform, setPlatform]         = useState('');
  const [poDate, setPoDate]             = useState(todayISO());
  const [lines, setLines]               = useState([blankLine(1)]);
  const [seq, setSeq]                   = useState(1);

  const [openSkuLine, setOpenSkuLine]       = useState(null);
  const [searchResults, setSearchResults]   = useState([]);
  const [dropdownRect, setDropdownRect]     = useState(null);

  const [previewOpen, setPreviewOpen]   = useState(false);
  const [generating, setGenerating]     = useState(false);
  const [toast, setToast]               = useState('');

  const [editingPoNumber, setEditingPoNumber] = useState(null);
  const [view, setView] = useState('home'); // 'home' | 'form'
  const [pendingCount, setPendingCount] = useState(0);

  const toastTimer = useRef(null);
  const closeTimer = useRef(null);
  const suppressLineResetRef = useRef(false);
  const kamName = user?.display_name || user?.username || 'KAM Portal';

  const multiRegion = regions.length > 1;
  const singleRegion = regions.length === 1 ? regions[0] : null;
  const locationDisplay = regions.length === 0 ? null
    : regions.length === 1 ? formatRegionLocation(singleRegion)
    : regions.map(formatRegionLocation).join('; ');

  function showToast(msg) {
    setToast(msg);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(''), 4200);
  }

  function loadPendingCount() {
    fetch(`${BASE}/distributor-po?action=pending-count&created_by=${encodeURIComponent(kamName)}`)
      .then(r => r.json()).then(d => setPendingCount(d.count || 0))
      .catch(() => {});
  }
  useEffect(() => { loadPendingCount(); }, [kamName]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetch(`${BASE}/distributor-po?action=distributors`)
      .then(r => r.json()).then(d => setDistributors(Array.isArray(d) ? d : []))
      .catch(() => setDistributors([]));
  }, []);

  // Deep-link from the history page's "Edit" action: /dms/distributor-po?edit=PO_NUMBER
  useEffect(() => {
    const editTarget = searchParams.get('edit');
    if (editTarget) {
      loadForEdit(editTarget);
      searchParams.delete('edit');
      setSearchParams(searchParams, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setPlatforms([]); setRegions([]);
    // Any SKU lines (and the selected platform) already on the form belong to
    // the PREVIOUS distributor's catalog and aren't valid for a new one —
    // clear them out, except while loadForEdit is programmatically setting
    // distributor/platform/lines together to open an existing PO (that path
    // owns the reset itself; letting this effect also reset `platform` here
    // clobbered the just-loaded value on the very next commit).
    if (!suppressLineResetRef.current) {
      setPlatform('');
      setLines([blankLine(1)]); setSeq(1);
    }
    if (!distributor) return;
    fetch(`${BASE}/distributor-po?action=platforms&distributor=${encodeURIComponent(distributor)}`)
      .then(r => r.json()).then(d => setPlatforms(Array.isArray(d) ? d : []))
      .catch(() => setPlatforms([]));
  }, [distributor]);

  useEffect(() => {
    setRegions([]);
    if (!suppressLineResetRef.current) { setLines([blankLine(1)]); setSeq(1); }
    if (!distributor || !platform) return;
    fetch(`${BASE}/distributor-po?action=regions&distributor=${encodeURIComponent(distributor)}&platform=${encodeURIComponent(platform)}`)
      .then(r => r.json()).then(d => setRegions(Array.isArray(d) ? d : []))
      .catch(() => setRegions([]));
  }, [distributor, platform]);

  async function searchSku(query) {
    if (!distributor || !platform) return setSearchResults([]);
    try {
      const res  = await fetch(`${BASE}/distributor-po?action=products&distributor=${encodeURIComponent(distributor)}&platform=${encodeURIComponent(platform)}&search=${encodeURIComponent(query)}`);
      const data = await res.json();
      setSearchResults(Array.isArray(data) ? data : []);
    } catch (e) { setSearchResults([]); }
  }

  // Rendered once at the page root, position:fixed anchored to whichever SKU
  // input is focused — same technique the old page used to dodge the table
  // wrapper's overflow-x clipping overflow-y too (per the CSS spec).
  function openSearch(lineId, query, inputEl) {
    clearTimeout(closeTimer.current);
    setOpenSkuLine(lineId);
    if (inputEl) {
      const r = inputEl.getBoundingClientRect();
      setDropdownRect({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 280) });
    }
    searchSku(query);
  }
  function scheduleCloseSearch() { closeTimer.current = setTimeout(() => setOpenSkuLine(null), 150); }

  function patchLine(id, patch) { setLines(prev => prev.map(l => (l.id === id ? { ...l, ...patch } : l))); }
  function addLine() { const id = seq + 1; setSeq(id); setLines(prev => [...prev, blankLine(id)]); }
  function removeLine(id) { setLines(prev => (prev.length === 1 ? [blankLine(prev[0].id)] : prev.filter(l => l.id !== id))); }

  function selectSku(lineId, sku) {
    patchLine(lineId, {
      ean: sku.ean, sku_name: sku.sku_name, query: sku.sku_name,
      mrp: sku.mrp ?? 0, case_pack: sku.case_pack ?? '',
      platform_margin_pct: sku.platform_margin_pct ?? 0,
      distributor_margin_pct: sku.distributor_margin_pct ?? 0,
    });
    clearTimeout(closeTimer.current);
    setOpenSkuLine(null);
  }

  const activeLines = lines.filter(l => l.ean);
  const priced = activeLines.map(l => ({ ...l, ...priceLine(l, multiRegion) }));
  const totalUnits  = priced.reduce((s, l) => s + l.qty, 0);
  const totalAmount = priced.reduce((s, l) => s + l.amount, 0);

  function resetForm() {
    setDistributor(''); setPlatform(''); setPoDate(todayISO());
    setLines([blankLine(seq + 1)]); setSeq(seq + 1);
    setEditingPoNumber(null);
  }

  function startNewPO() {
    resetForm();
    setView('form');
  }

  function backToHome() {
    const hasUnsavedWork = !!distributor || !!platform || activeLines.length > 0;
    if (hasUnsavedWork && !window.confirm('Going back will reset the fields you\'ve filled in — continue?')) return;
    resetForm();
    setView('home');
  }

  function validate() {
    if (!distributor) return 'Select a distributor first';
    if (!platform) return 'Select a platform';
    if (activeLines.length === 0) return 'Add at least one SKU';
    for (const l of priced) {
      if (!(l.qty > 0)) return `Enter a quantity for ${l.sku_name || l.ean}`;
    }
    return null;
  }

  function openPreview() {
    const err = validate();
    if (err) return showToast(err);
    setPreviewOpen(true);
  }

  function buildLinesPayload() {
    return priced.map(l => ({
      ean: l.ean, sku_name: l.sku_name, mrp: num(l.mrp), case_pack: l.case_pack || null,
      platform_margin_pct: num(l.platform_margin_pct), distributor_margin_pct: num(l.distributor_margin_pct),
      qty: l.qty, region_qty: multiRegion ? l.regionQty : null,
    }));
  }

  async function generate() {
    const err = validate();
    if (err) { setPreviewOpen(false); return showToast(err); }
    setGenerating(true);
    try {
      const body = editingPoNumber
        ? { po_number: editingPoNumber, lines: buildLinesPayload() }
        : {
            party_name: distributor, channel: platform, po_date: poDate,
            location: locationDisplay, kam_name: kamName, lines: buildLinesPayload(),
          };
      const res  = await fetch(`${BASE}/distributor-po?action=${editingPoNumber ? 'update' : 'generate'}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || !data.success) return showToast(data.error || 'Could not save PO');

      setPreviewOpen(false);
      showToast(editingPoNumber ? `Purchase Order ${data.po_number} updated` : `Purchase Order ${data.po_number} generated`);
      resetForm();
      setView('home');
      loadPendingCount();
    } catch (e) {
      showToast('Network error saving PO');
    } finally {
      setGenerating(false);
    }
  }

  async function loadForEdit(poNumber) {
    try {
      const res  = await fetch(`${BASE}/distributor-po?action=detail&po_number=${encodeURIComponent(poNumber)}`);
      const data = await res.json();
      if (!res.ok) return showToast(data.error || 'Could not load PO');

      // Suppress the distributor/platform-change effects' own line-reset
      // while we set all three together — otherwise the platform effect
      // would immediately wipe the lines we're about to load.
      suppressLineResetRef.current = true;
      setDistributor(data.header.party_name);
      setPlatform(data.header.channel);
      setPoDate(data.header.po_date ? String(data.header.po_date).slice(0, 10) : todayISO());
      const newLines = data.lines.map((l, i) => ({
        id: i + 1, ean: l.ean_code, sku_name: l.product_name, query: l.product_name,
        mrp: l.mrp, case_pack: l.case_qty ?? '',
        platform_margin_pct: l.platform_margin_pct ?? 0, distributor_margin_pct: l.distributor_margin_pct ?? 0,
        qty: l.region_qty ? '' : l.po_qty, regionQty: l.region_qty || {},
      }));
      setLines(newLines.length ? newLines : [blankLine(1)]);
      setSeq(newLines.length);
      setEditingPoNumber(poNumber);
      setView('form');
      // Deliberately left `true` — cleared only by the distributor/platform
      // selects' own onChange handlers below (a genuine user-driven change),
      // not on a timer. A setTimeout(…, 0) here was found to fire BEFORE
      // React's passive effects for this same commit, so the distributor-
      // and platform-change effects saw the flag already false and reset
      // platform/lines right back to blank — the timer lost the race.
      showToast(`Editing ${poNumber} — add/remove SKUs or change quantities, then Save Changes`);
    } catch (e) { showToast('Network error loading PO'); }
  }

  function downloadCurrentDraftExcel() {
    exportPoToExcel({
      header: {
        po_number: editingPoNumber || 'DRAFT', po_date: poDate, party_name: distributor,
        channel: platform, location: locationDisplay,
      },
      lines: priced.map(l => ({
        ean_code: l.ean, product_name: l.sku_name, case_qty: l.case_pack, po_qty: l.qty,
        mrp: l.mrp, platform_billing_price: l.platformBillingPrice,
        distributor_commission: l.distributorCommission, billing_price: l.billingPrice, po_amount: l.amount,
        region_qty: multiRegion ? l.regionQty : null,
      })),
    });
  }

  return (
    <div className="dpo-page" data-tutorial="distributor-po-table" style={S.page}>
      <style>{EMBEDDED_CSS}</style>

      <div style={S.headerBar}>
        <DpoBeamBackground />
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, position: 'relative', zIndex: 1 }}>
          {view === 'form' && (
            <button style={S.backBtn} onClick={backToHome}><ArrowLeft size={ICON.sm} />Back</button>
          )}
          <h1 style={{ ...S.h1 }}>Distributor <span style={{ fontWeight: 600, color: '#C9B7EA' }}>Purchase Orders</span></h1>
        </div>
      </div>

      {view === 'home' && (
        <div style={S.homeGrid}>
          <button style={S.homeOptionCard} onClick={() => navigate('/dms/distributor-po/history')}>
            <span style={S.homeOptionIcon}><FileClock size={28} /></span>
            <div>
              <div style={S.homeOptionTitle}>My Recent Purchase Orders</div>
              <div style={S.homeOptionSub}>View, edit, print, or classify your drafted POs</div>
            </div>
          </button>
          <button style={S.homeOptionCard} onClick={startNewPO}>
            <span style={S.homeOptionIcon}><Plus size={28} /></span>
            <div>
              <div style={S.homeOptionTitle}>Add a New Purchase Order</div>
              <div style={S.homeOptionSub}>Create a fresh PO for a distributor</div>
            </div>
          </button>
          <button style={S.pendingCard} onClick={() => navigate('/dms/distributor-po/history?status=pending')}>
            <span style={S.homeOptionIcon}><ClipboardList size={22} /></span>
            <div style={S.statLabel}>Pending Action</div>
            <div style={S.pendingCount}>{pendingCount}</div>
            <div style={S.homeOptionSub}>Purchase Order{pendingCount === 1 ? '' : 's'} awaiting distributor approval or classification</div>
          </button>
        </div>
      )}

      {view === 'form' && (
      <>
      {editingPoNumber && (
        <div style={S.editBanner}>
          <Pencil size={ICON.sm} />
          Editing <b style={{ fontFamily: 'monospace' }}>{editingPoNumber}</b> — distributor/platform are fixed; add, remove, or resize SKU lines below.
          <button style={S.linkBtn} onClick={resetForm}>Cancel edit</button>
        </div>
      )}

      {/* Order details */}
      <div style={{ ...S.card, ...(editingPoNumber ? S.cardEditing : {}) }}>
        <SectionTitle n={1} title="Order Details" />
        <div style={S.bentoGrid}>
          <div style={S.bentoCard}>
            <div style={S.statLabel}>Distributor *</div>
            <select style={S.select} value={distributor} disabled={!!editingPoNumber}
              onChange={e => { suppressLineResetRef.current = false; setDistributor(e.target.value); }}>
              <option value="">Select distributor</option>
              {distributors.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div style={S.bentoCard}>
            <div style={S.statLabel}>Platform *</div>
            <select style={S.select} value={platform} disabled={!distributor || !!editingPoNumber}
              onChange={e => { suppressLineResetRef.current = false; setPlatform(e.target.value); }}>
              <option value="">{distributor ? 'Select platform' : 'Pick a distributor first'}</option>
              {platforms.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div style={S.bentoCard}>
            <div style={S.statLabel}>PO Date</div>
            <input type="date" style={S.select} value={poDate} onChange={e => setPoDate(e.target.value)} />
          </div>
          <div style={S.bentoCard}>
            <div style={S.statLabel}>Location</div>
            {!distributor || !platform ? (
              <div style={S.mutedNote}>Pick a distributor and platform to see its location</div>
            ) : regions.length === 0 ? (
              <div style={S.warnNote}><AlertCircle size={ICON.sm} />Not configured in distributor_master for this platform yet</div>
            ) : multiRegion ? (
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--dpo-text)' }}>{locationDisplay} <span style={{ color: 'var(--dpo-text-muted)', fontWeight: 500 }}>— entered per region below</span></div>
            ) : (
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--dpo-text)' }}>{locationDisplay}</div>
            )}
          </div>
        </div>
      </div>

      {/* Product line items */}
      <div style={{ ...S.card, ...(editingPoNumber ? S.cardEditing : {}) }}>
        <SectionTitle n={2} title="Product Details" sub={multiRegion ? 'Enter quantity per region — Total Amount sums across all regions' : 'Search by EAN or SKU name'} />
        <div style={{ overflowX: 'auto' }}>
          <table style={S.table}>
            <thead>
              <tr>
                {['#', 'SKU *', 'EAN *', 'Case Pack', 'MRP', 'Platform Billing Price', 'Distributor Commission', 'Billing Price',
                  ...(multiRegion ? regions.map(r => r.region) : ['Quantity *']), 'Total Amount', ''].map(h => (
                  <th key={h} style={S.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => {
                const p = priceLine(l, multiRegion);
                return (
                  <tr key={l.id}>
                    <td style={S.td}>{i + 1}</td>
                    <td style={{ ...S.td, minWidth: 240 }}>
                      <input style={S.input} value={l.query} placeholder="Search SKU..."
                        onChange={e => { patchLine(l.id, { query: e.target.value }); openSearch(l.id, e.target.value, e.target); }}
                        onFocus={e => openSearch(l.id, l.query, e.target)}
                        onBlur={scheduleCloseSearch} />
                    </td>
                    <td style={{ ...S.td, minWidth: 130 }}>
                      <input style={S.input} value={l.ean} placeholder="EAN" readOnly />
                    </td>
                    <td style={{ ...S.td, minWidth: 90 }}><div style={S.readCell}>{l.case_pack || '—'}</div></td>
                    <td style={{ ...S.td, minWidth: 90 }}><div style={S.readCell}>{fmtINR(num(l.mrp))}</div></td>
                    <td style={{ ...S.td, minWidth: 120 }}><div style={S.readCell}>{fmtINR(p.platformBillingPrice)}</div></td>
                    <td style={{ ...S.td, minWidth: 120 }}><div style={S.readCell}>{fmtINR(p.distributorCommission)}</div></td>
                    <td style={{ ...S.td, minWidth: 110 }}><div style={{ ...S.readCell, fontWeight: 700, color: 'var(--dpo-purple)' }}>{fmtINR(p.billingPrice)}</div></td>
                    {multiRegion ? regions.map(r => (
                      <td key={r.region} style={{ ...S.td, minWidth: 90 }}>
                        <input style={{ ...S.input, textAlign: 'right' }} value={l.regionQty[r.region] || ''}
                          onChange={e => patchLine(l.id, { regionQty: { ...l.regionQty, [r.region]: e.target.value.replace(/[^0-9]/g, '') } })} />
                      </td>
                    )) : (
                      <td style={{ ...S.td, minWidth: 90 }}>
                        <input style={{ ...S.input, textAlign: 'right' }} value={l.qty}
                          onChange={e => patchLine(l.id, { qty: e.target.value.replace(/[^0-9]/g, '') })} />
                      </td>
                    )}
                    <td style={{ ...S.td, fontWeight: 700, textAlign: 'right', color: 'var(--dpo-green)' }}>{fmtINR(p.amount)}</td>
                    <td style={S.td}><button onClick={() => removeLine(l.id)} style={S.smallBtn}><X size={ICON.xs} /></button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14 }}>
          <button onClick={addLine} style={S.btnGhost}><Plus size={ICON.sm} />Add SKU</button>
          <div style={{ fontSize: 14, color: 'var(--dpo-text)' }}>
            <b>{fmtNum(totalUnits)}</b> units &middot; Total <b>{fmtINR(totalAmount)}</b>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 8 }}>
        <button onClick={openPreview} style={{ ...S.btnGhost, padding: '14px 24px', fontSize: 14 }}>
          {editingPoNumber ? 'Preview Changes' : 'Preview Purchase Order'}
        </button>
      </div>
      </>
      )}

      {/* Preview modal — widened so every Product Details column fits without
          scrolling, and the line-items table mirrors the full column set
          from the main form (not just a shortened subset). */}
      {previewOpen && (
        <div style={S.overlay} onClick={() => setPreviewOpen(false)}>
          <div style={{ ...S.modal, maxWidth: 1400, width: '95vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column', padding: 0 }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '22px 28px', borderBottom: '1px solid var(--dpo-border)', flex: 'none' }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--dpo-text)' }}>{editingPoNumber ? 'Preview Changes' : 'Preview Purchase Order'}</div>
              <div style={{ fontSize: 12.5, color: 'var(--dpo-text-muted)' }}>This won't be sent to Accounts yet — that happens later from My Recent POs, once the distributor confirms.</div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 28px' }}>
              <div style={{ ...S.bentoGrid, marginBottom: 18 }}>
                {[['Distributor', distributor], ['Platform', platform], ['Location', locationDisplay || '—'], ['Total Units', fmtNum(totalUnits)]].map(([label, value]) => (
                  <div key={label} style={S.bentoCard}>
                    <div style={S.statLabel}>{label}</div>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--dpo-text)', marginTop: 3 }}>{value}</div>
                  </div>
                ))}
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={S.table}>
                  <thead>
                    <tr>
                      {['SKU', 'EAN', 'Case Pack', 'MRP', 'Platform Billing Price', 'Distributor Commission', 'Billing Price',
                        ...(multiRegion ? regions.map(r => r.region) : ['Quantity']), 'Amount'].map(h => <th key={h} style={S.th}>{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {priced.map(l => (
                      <tr key={l.id}>
                        <td style={S.td}>{l.sku_name}</td>
                        <td style={S.td}>{l.ean}</td>
                        <td style={S.td}>{l.case_pack || '—'}</td>
                        <td style={S.td}>{fmtINR(num(l.mrp))}</td>
                        <td style={S.td}>{fmtINR(l.platformBillingPrice)}</td>
                        <td style={S.td}>{fmtINR(l.distributorCommission)}</td>
                        <td style={{ ...S.td, fontWeight: 700 }}>{fmtINR(l.billingPrice)}</td>
                        {multiRegion ? regions.map(r => (
                          <td key={r.region} style={S.td}>{fmtNum(l.regionQty[r.region])}</td>
                        )) : (
                          <td style={S.td}>{fmtNum(l.qty)}</td>
                        )}
                        <td style={{ ...S.td, fontWeight: 700 }}>{fmtINR(l.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div style={{ flex: 'none', padding: '18px 28px', borderTop: '1px solid var(--dpo-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 16, fontWeight: 700 }}>Grand Total: <span style={{ color: 'var(--dpo-green)' }}>{fmtINR(totalAmount)}</span></div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => setPreviewOpen(false)} style={S.btnGhost}>Back to Edit</button>
                <button onClick={downloadCurrentDraftExcel} style={S.btnGhost}><Download size={ICON.sm} />Download Excel</button>
                <button onClick={generate} disabled={generating} style={{ ...S.btnPrimary, opacity: generating ? 0.7 : 1 }}>
                  {generating ? 'Saving…' : editingPoNumber ? 'Save Changes' : 'Confirm & Generate PO'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {openSkuLine != null && dropdownRect && (
        <SkuDropdown rect={dropdownRect} results={searchResults} onSelect={sku => selectSku(openSkuLine, sku)} />
      )}

      {toast && <div style={S.toast}>{toast}</div>}
    </div>
  );
}

function SkuDropdown({ rect, results, onSelect }) {
  return (
    <div style={{ ...S.dropdown, top: rect.top, left: rect.left, width: rect.width }} onMouseDown={e => e.preventDefault()}>
      {results.map(r => (
        <div key={r.ean} onMouseDown={() => onSelect(r)} style={S.dropdownItem}>
          <div style={{ fontWeight: 600 }}>{r.sku_name}</div>
          <div style={{ fontSize: 11.5, color: 'var(--dpo-text-muted)' }}>EAN {r.ean} &middot; MRP {fmtINR(r.mrp)}</div>
        </div>
      ))}
      {results.length === 0 && <div style={{ padding: '10px 11px', fontSize: 12.5, color: 'var(--dpo-text-muted)' }}>Not in the catalog — ask an admin to add it to distributor_product_master.</div>}
    </div>
  );
}
